import { createRouter as createVueRouter, createWebHistory, START_LOCATION } from 'vue-router';
import { watch } from 'vue';
import routes from './routes';
import { auth0, authCallbackError, redirectToLogin } from '@/auth0';
import { useGlobalStore } from '@/stores/global';

const setTitle = () => {
    setTimeout(() => {
        document.title = `Feature Flag Service (${window.__KEAP__.VUE_APP_ENV})`;
    });
};

/**
 * Builds the /auth-error route string with error query params.
 */
export const getAuthErrorRoute = (authError) => {
    const { error, error_description: errorDescription } = authError || {};
    const query = new URLSearchParams();

    if (error) query.set('error', error);
    if (errorDescription) query.set('error_description', errorDescription);

    const queryString = query.toString();

    return `/auth-error${queryString ? `?${queryString}` : ''}`;
};

// On a full page load the Auth0 SDK is still exchanging the authorization code
// (PKCE) and restoring the session; isLoading stays true until that finishes.
// Resolve once it's done so the auth check below sees the settled auth state.
// Reject after a timeout so a hung SDK/network can't leave the guard waiting
// forever on a blank page — the caller falls back to a login redirect.
const waitForAuth0ToFinishPKCEFlow = (timeoutMs = 10_000) => new Promise((resolve, reject) => {
    if (!auth0.isLoading.value) {
        resolve();

        return;
    }

    const timer = setTimeout(() => {
        stop();
        reject(new Error('Auth0 load timeout'));
    }, timeoutMs);

    const stop = watch(auth0.isLoading, (loading) => {
        if (!loading) {
            clearTimeout(timer);
            stop();
            resolve();
        }
    });
});

/**
 * Global beforeEach guard.
 * Public routes skip auth; Auth0 errors redirect to /auth-error (circuit breaker);
 * unauthenticated users are sent to login.
 */
export const requireAuth = async (to, from) => {
    if (to.name === 'logout') {
        auth0.logout({ logoutParams: { returnTo: window.location.origin } });

        return false;
    }

    // Public routes (e.g. /auth-error) skip auth entirely.
    if (to.meta?.isPublic) return true;

    // START_LOCATION is the first navigation of a fresh page load, where the
    // SDK may still be mid-PKCE; wait before checking auth. Subsequent in-app
    // navigations reuse the already-settled session, so no wait is needed.
    if (from === START_LOCATION) {
        try {
            await waitForAuth0ToFinishPKCEFlow();
        } catch {
            // SDK never settled; bounce to login rather than hang on a blank page.
            await redirectToLogin(to.fullPath);

            return false;
        }
    }

    // Circuit breaker: when Auth0 returns a terminal login/callback error
    // (e.g. locked account, missing permissions), the authorize redirect comes
    // back with ?error=&state= query params. Those are captured once at boot as
    // authCallbackError (before the SDK strips the URL). Without this early
    // exit, redirectToLogin would trigger loginWithRedirect, Auth0 would return
    // the same error, and the cycle would repeat indefinitely. Redirecting to
    // /auth-error (a public route) breaks the loop.
    // Do NOT key this off auth0.error — that shared SDK ref is also set by
    // transient failures like getAccessTokenSilently (login_required), which
    // must still recover via loginWithRedirect.
    if (authCallbackError) {
        return getAuthErrorRoute(authCallbackError);
    }

    if (!auth0.isAuthenticated.value) {
        await redirectToLogin(to.fullPath);

        return false;
    }

    return true;
};

export const createRouter = () => {
    const router = createVueRouter({
        history: createWebHistory(),
        routes,
    });

    router.beforeEach((to, from) => requireAuth(to, from));

    router.beforeResolve((to, _, next) => {
        setTitle(to);

        useGlobalStore().setNavOpen(false);

        next();
    });

    return router;
};
